import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";


const emptyBankAccount = {
  bank_name: "",
  branch_name: "",
  branch_code: "",
  account_type: "普通",
  account_number: "",
  account_holder: "",
};

const getJapanDate = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(new Date());

const getJapanMonth = () => getJapanDate().slice(0, 7);

const buildInvoiceRequestForm = () => ({
  settlement_amount: "",
  request_month: getJapanMonth(),
  note: "",
});

const buildSettlementForm = (prepared) => ({
  settlement_amount: String(prepared?.order?.original_dispatch_price ?? ""),
  expense_category: "parking",
  adjustment_reason: "",
  bank_account: {
    ...emptyBankAccount,
    ...(prepared?.default_bank_account || {}),
  },
});

const buildEditForm = (settlement) => ({
  settlement_amount: String(settlement?.settlement_amount ?? ""),
  expense_category: settlement?.expense_category || "parking",
  adjustment_reason: settlement?.adjustment_reason || "",
  bank_account: {
    ...emptyBankAccount,
    ...(settlement?.bank_account || {}),
  },
});

const getDownloadFilename = (response, fallbackName) => {
  const disposition = response.headers?.["content-disposition"] || "";
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  return encodedMatch ? decodeURIComponent(encodedMatch[1]) : fallbackName;
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (requestError, fallback) => {
  if (requestError.response?.data instanceof Blob) {
    try {
      const text = await requestError.response.data.text();
      return JSON.parse(text)?.detail || fallback;
    } catch {
      return fallback;
    }
  }
  return requestError.response?.data?.detail || fallback;
};

export default function useAdminSettlements({ apiBaseUrl, activeTab, user, onOrderRefresh }) {
  const [month, setMonth] = useState(getJapanMonth);
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ pending: [], history: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(buildSettlementForm());
  const [bankRawText, setBankRawText] = useState("");
  const [aiParsing, setAiParsing] = useState(false);
  const [aiReview, setAiReview] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferDate, setTransferDate] = useState(getJapanDate);
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [financeForm, setFinanceForm] = useState({ name: "", email: "", password: "" });
  const [exporting, setExporting] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderSearchResults, setOrderSearchResults] = useState([]);
  const [orderSearching, setOrderSearching] = useState(false);
  const [invoiceRequestForm, setInvoiceRequestForm] = useState(buildInvoiceRequestForm);
  const [invoiceRequestFile, setInvoiceRequestFile] = useState(null);
  const [invoiceRequestUploading, setInvoiceRequestUploading] = useState(false);
  const [invoiceOrderSearch, setInvoiceOrderSearch] = useState("");
  const [invoiceOrderSearchResults, setInvoiceOrderSearchResults] = useState([]);
  const [invoiceOrderSearching, setInvoiceOrderSearching] = useState(false);
  const [selectedInvoiceOrderIds, setSelectedInvoiceOrderIds] = useState([]);
  const [invoiceLinkModal, setInvoiceLinkModal] = useState(null);
  const [invoiceLinkSearch, setInvoiceLinkSearch] = useState("");
  const [invoiceLinkSearchResults, setInvoiceLinkSearchResults] = useState([]);
  const [invoiceLinkSearching, setInvoiceLinkSearching] = useState(false);
  const [invoiceLinkSelectedOrderIds, setInvoiceLinkSelectedOrderIds] = useState([]);
  const [invoiceLinkSaving, setInvoiceLinkSaving] = useState(false);
  const [invoiceRequestDownloadMonth, setInvoiceRequestDownloadMonth] = useState(getJapanMonth);
  const [selectedInvoiceRequestIds, setSelectedInvoiceRequestIds] = useState([]);
  const [invoiceRequestDownloading, setInvoiceRequestDownloading] = useState("");
  const settlementRequestSequence = useRef(0);

  const isAdmin = user?.role === "admin";
  const isFinance = user?.role === "finance";
  const canManageInvoiceLinks = isAdmin || isFinance;

  const fetchSettlements = useCallback(async () => {
    if (!user || !["admin", "finance"].includes(user.role)) return;
    const requestId = settlementRequestSequence.current + 1;
    settlementRequestSequence.current = requestId;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements`, {
        params: { month, search: search.trim() },
      });
      if (requestId === settlementRequestSequence.current) {
        setData(response.data || { pending: [], history: [], summary: {} });
      }
    } catch (requestError) {
      if (requestId === settlementRequestSequence.current) {
        setError(requestError.response?.data?.detail || "结算清单加载失败");
      }
    } finally {
      if (requestId === settlementRequestSequence.current) {
        setLoading(false);
      }
    }
  }, [apiBaseUrl, month, search, user]);

  const fetchFinanceAccounts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/finance/accounts`);
      setFinanceAccounts(response.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "财务账号加载失败");
    }
  }, [apiBaseUrl, isAdmin]);

  const searchInvoiceRequestOrders = async () => {
    if (!isAdmin) return;
    setInvoiceOrderSearching(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/invoice-requests/orders/search`, {
        params: { search: invoiceOrderSearch.trim() },
      });
      setInvoiceOrderSearchResults(response.data || []);
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "可绑定订单查找失败");
    } finally {
      setInvoiceOrderSearching(false);
    }
  };

  const toggleInvoiceOrderSelection = (orderId) => {
    setSelectedInvoiceOrderIds((current) => (
      current.includes(orderId)
        ? current.filter((value) => value !== orderId)
        : [...current, orderId]
    ));
  };

  const openInvoiceOrderLinker = (settlement) => {
    if (!canManageInvoiceLinks || settlement?.settlement_type !== "invoice_request") return;
    const linkedOrders = Array.isArray(settlement.linked_orders) ? settlement.linked_orders : [];
    setInvoiceLinkModal(settlement);
    setInvoiceLinkSearch("");
    setInvoiceLinkSearchResults(linkedOrders);
    setInvoiceLinkSelectedOrderIds(linkedOrders.map((order) => order.id).filter(Boolean));
  };

  const closeInvoiceOrderLinker = () => {
    if (invoiceLinkSaving) return;
    setInvoiceLinkModal(null);
    setInvoiceLinkSearch("");
    setInvoiceLinkSearchResults([]);
    setInvoiceLinkSelectedOrderIds([]);
  };

  const searchInvoiceLinkOrders = async () => {
    if (!canManageInvoiceLinks || !invoiceLinkModal) return;
    setInvoiceLinkSearching(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/invoice-requests/orders/search`, {
        params: { search: invoiceLinkSearch.trim() },
      });
      setInvoiceLinkSearchResults(response.data || []);
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "可绑定订单查找失败");
    } finally {
      setInvoiceLinkSearching(false);
    }
  };

  const toggleInvoiceLinkOrderSelection = (orderId) => {
    setInvoiceLinkSelectedOrderIds((current) => (
      current.includes(orderId)
        ? current.filter((value) => value !== orderId)
        : [...current, orderId]
    ));
  };

  const clearInvoiceLinkOrderSelection = () => setInvoiceLinkSelectedOrderIds([]);

  const saveInvoiceOrderLinks = async () => {
    if (!invoiceLinkModal?.id || !canManageInvoiceLinks) return;
    setInvoiceLinkSaving(true);
    try {
      await axios.put(
        `${apiBaseUrl}/admin/settlements/invoice-requests/${invoiceLinkModal.id}/orders`,
        { order_ids: invoiceLinkSelectedOrderIds },
      );
      await fetchSettlements();
      setInvoiceLinkModal(null);
      setInvoiceLinkSearch("");
      setInvoiceLinkSearchResults([]);
      setInvoiceLinkSelectedOrderIds([]);
      window.alert(invoiceLinkSelectedOrderIds.length ? "请求书绑定订单已更新" : "请求书的订单绑定已解除");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "请求书绑定订单保存失败");
    } finally {
      setInvoiceLinkSaving(false);
    }
  };

  const searchSettlementOrders = async () => {
    if (!isAdmin) return;
    setOrderSearching(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/orders/search`, {
        params: { search: orderSearch.trim() },
      });
      setOrderSearchResults(response.data || []);
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "订单查找失败");
    } finally {
      setOrderSearching(false);
    }
  };

  const exportMonthlySettlements = async (format) => {
    if (!month || !["xlsx", "pdf"].includes(format)) return;
    setExporting(format);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/export.${format}`, {
        params: { month, search: search.trim() },
        responseType: "blob",
      });
      const fallbackName = `settlement_transfers_${month}.${format}`;
      triggerBlobDownload(response.data, getDownloadFilename(response, fallbackName));
    } catch (requestError) {
      window.alert(await getBlobErrorMessage(requestError, "导出财务转账账单失败"));
    } finally {
      setExporting("");
    }
  };

  const exportMonthlyPaymentStatements = async () => {
    if (!month) return;
    setExporting("payment-statements");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/payment-statements.pdf`, {
        params: { month, search: search.trim() },
        responseType: "blob",
      });
      const fallbackName = `支払明細書_${month}.pdf`;
      triggerBlobDownload(response.data, getDownloadFilename(response, fallbackName));
    } catch (requestError) {
      window.alert(await getBlobErrorMessage(requestError, "生成支払明細書失败"));
    } finally {
      setExporting("");
    }
  };

  const submitInvoiceRequest = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!isAdmin) return;
    const amount = Number(invoiceRequestForm.settlement_amount);
    if (!Number.isInteger(amount) || amount <= 0) {
      window.alert("请求书金额必须是大于0的整数日元");
      return;
    }
    if (!invoiceRequestFile) {
      window.alert("请选择要上传的请求书文件");
      return;
    }
    const formData = new FormData();
    formData.append("settlement_amount", String(amount));
    formData.append("request_month", invoiceRequestForm.request_month);
    formData.append("note", invoiceRequestForm.note.trim());
    formData.append("order_ids", JSON.stringify(selectedInvoiceOrderIds));
    formData.append("request_file", invoiceRequestFile);
    setInvoiceRequestUploading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements/invoice-requests/upload`, formData);
      setInvoiceRequestForm(buildInvoiceRequestForm());
      setInvoiceRequestFile(null);
      setInvoiceOrderSearch("");
      setInvoiceOrderSearchResults([]);
      setSelectedInvoiceOrderIds([]);
      formElement.reset();
      await fetchSettlements();
      window.alert("请求书已上传并加入待财务处理");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "请求书上传失败");
    } finally {
      setInvoiceRequestUploading(false);
    }
  };

  const downloadInvoiceRequest = async (settlement) => {
    if (!settlement?.id) return;
    setInvoiceRequestDownloading(settlement.id);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/invoice-requests/${settlement.id}/download`, {
        responseType: "blob",
      });
      const fallbackName = settlement.request_document?.original_filename || `${settlement.request_no || settlement.id}.pdf`;
      triggerBlobDownload(response.data, getDownloadFilename(response, fallbackName));
    } catch (requestError) {
      window.alert(await getBlobErrorMessage(requestError, "请求书下载失败"));
    } finally {
      setInvoiceRequestDownloading("");
    }
  };

  const toggleInvoiceRequestSelection = (settlementId) => {
    setSelectedInvoiceRequestIds((current) => (
      current.includes(settlementId)
        ? current.filter((value) => value !== settlementId)
        : [...current, settlementId]
    ));
  };

  const downloadSelectedInvoiceRequests = async () => {
    if (!selectedInvoiceRequestIds.length) {
      window.alert("请至少勾选一个请求书");
      return;
    }
    setInvoiceRequestDownloading("selected");
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/settlements/invoice-requests/download-selected`,
        { settlement_ids: selectedInvoiceRequestIds },
        { responseType: "blob" },
      );
      triggerBlobDownload(
        response.data,
        getDownloadFilename(response, `invoice_requests_selected_${selectedInvoiceRequestIds.length}.zip`),
      );
    } catch (requestError) {
      window.alert(await getBlobErrorMessage(requestError, "已选请求书下载失败"));
    } finally {
      setInvoiceRequestDownloading("");
    }
  };

  const downloadMonthlyInvoiceRequests = async () => {
    if (!invoiceRequestDownloadMonth) return;
    setInvoiceRequestDownloading("month");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/invoice-requests/download-month.zip`, {
        params: { month: invoiceRequestDownloadMonth },
        responseType: "blob",
      });
      triggerBlobDownload(
        response.data,
        getDownloadFilename(response, `invoice_requests_${invoiceRequestDownloadMonth}.zip`),
      );
    } catch (requestError) {
      window.alert(await getBlobErrorMessage(requestError, "整月请求书下载失败"));
    } finally {
      setInvoiceRequestDownloading("");
    }
  };

  useEffect(() => {
    if (activeTab !== "settlements") return;
    fetchSettlements();
    fetchFinanceAccounts();
  }, [activeTab, fetchFinanceAccounts, fetchSettlements]);

  const openCreateSettlement = async (order) => {
    if (!order?.id || !isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/order/${order.id}/prepare`);
      const prepared = response.data;
      if (prepared.existing_settlement) {
        const status = prepared.existing_settlement.status === "paid" ? "已转账" : "待财务处理";
        window.alert(`该订单已有结算记录：${status}`);
        return;
      }
      setForm(buildSettlementForm(prepared));
      setBankRawText("");
      setAiReview([]);
      setModal({ mode: "create", prepared, settlement: null, unlockToken: "" });
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "该订单暂时不能结算");
    } finally {
      setLoading(false);
    }
  };

  const openAdditionalExpense = async (order) => {
    if (!order?.id || !isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/settlements/order/${order.id}/prepare-additional`);
      const prepared = response.data;
      setForm({
        ...buildSettlementForm(prepared),
        settlement_amount: "",
        expense_category: "parking",
        adjustment_reason: "",
      });
      setBankRawText("");
      setAiReview([]);
      setModal({ mode: "additional-expense", prepared, settlement: null, unlockToken: "" });
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "该订单暂时不能登记附加费用");
    } finally {
      setLoading(false);
    }
  };

  const openPendingSettlementEdit = (settlement) => {
    if (!settlement?.id || !isAdmin) return;
    if (settlement.payable === false) {
      window.alert(settlement.blocking_reason || "该记录当前不能修改，请撤销后重新提交");
      return;
    }
    setForm(buildEditForm(settlement));
    setBankRawText("");
    setAiReview([]);
    setModal({
      mode: "pending-edit",
      prepared: {
        order: {
          id: settlement.order_id,
          order_no: settlement.order_no,
          driver_name: settlement.driver_name,
          departure_date: settlement.departure_date,
          pickup_time: settlement.pickup_time,
          route: settlement.route,
          original_dispatch_price: settlement.original_dispatch_price,
        },
      },
      settlement,
      unlockToken: "",
    });
  };

  const closeModal = () => {
    setModal(null);
    setBankRawText("");
    setAiReview([]);
  };

  const updateBankField = (field, value) => {
    setForm((current) => ({
      ...current,
      bank_account: { ...current.bank_account, [field]: value },
    }));
  };

  const parseBankAccountText = async () => {
    if (!bankRawText.trim()) {
      window.alert("请先粘贴银行账户文字");
      return;
    }
    setAiParsing(true);
    setAiReview([]);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/settlements/parse-bank-account-ai`, {
        raw_text: bankRawText,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.error || "AI识别失败");
      }
      const parsed = response.data.parsed || {};
      setForm((current) => ({
        ...current,
        bank_account: { ...current.bank_account, ...parsed },
      }));
      setAiReview(response.data.requires_review || []);
    } catch (requestError) {
      const message = requestError.response?.data?.detail || requestError.message || "AI识别失败，请手动填写";
      setAiReview([message]);
    } finally {
      setAiParsing(false);
    }
  };

  const submitSettlement = async () => {
    if (!modal?.prepared?.order?.id) return;
    const amount = Number(form.settlement_amount);
    if (!Number.isInteger(amount) || amount < 0) {
      window.alert("实际结算金额必须是0以上的整数日元");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements`, {
        order_id: modal.prepared.order.id,
        settlement_amount: amount,
        adjustment_reason: form.adjustment_reason,
        bank_account: form.bank_account,
      });
      closeModal();
      await fetchSettlements();
      onOrderRefresh?.();
      window.alert("已提交财务待转账清单");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "创建结算记录失败");
    } finally {
      setLoading(false);
    }
  };

  const submitAdditionalExpense = async () => {
    if (!modal?.prepared?.order?.id) return;
    const amount = Number(form.settlement_amount);
    if (!Number.isInteger(amount) || amount <= 0) {
      window.alert("附加费用金额必须是大于0的整数日元");
      return;
    }
    if (!form.adjustment_reason.trim()) {
      window.alert("请填写附加费用说明");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements/additional-expenses`, {
        order_id: modal.prepared.order.id,
        expense_category: form.expense_category,
        settlement_amount: amount,
        adjustment_reason: form.adjustment_reason,
        bank_account: form.bank_account,
      });
      closeModal();
      await fetchSettlements();
      await searchSettlementOrders();
      window.alert("附加费用已单独提交至财务待转账，原订单结算不变");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "附加费用提交失败");
    } finally {
      setLoading(false);
    }
  };

  const submitPendingSettlementUpdate = async () => {
    if (!modal?.settlement?.id) return;
    const amount = Number(form.settlement_amount);
    const requiresPositiveAmount = ["additional_expense", "invoice_request"].includes(modal.settlement.settlement_type);
    if (!Number.isInteger(amount) || (requiresPositiveAmount ? amount <= 0 : amount < 0)) {
      window.alert(requiresPositiveAmount ? "该类结算金额必须是大于0的整数日元" : "实际结算金额必须是0以上的整数日元");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/settlements/${modal.settlement.id}/pending`, {
        settlement_amount: amount,
        adjustment_reason: form.adjustment_reason,
        bank_account: form.bank_account,
      });
      closeModal();
      await fetchSettlements();
      onOrderRefresh?.();
      window.alert("待转账结算记录已更新");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "更新待转账记录失败");
    } finally {
      setLoading(false);
    }
  };

  const voidPendingSettlement = async (settlement) => {
    if (!settlement?.id || !isAdmin) return;
    const defaultReason = settlement.blocking_reason || "";
    const reason = window.prompt(
      "请输入撤销原因。撤销后该订单可以重新提交结算，原记录和操作历史会保留。",
      defaultReason,
    );
    if (!reason?.trim()) return;
    if (!window.confirm(`确认撤销订单 ${settlement.order_no} 的待转账记录吗？`)) return;
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements/${settlement.id}/void`, {
        reason: reason.trim(),
      });
      await fetchSettlements();
      onOrderRefresh?.();
      window.alert("待转账记录已撤销，该订单现在可以重新提交结算");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "撤销待转账记录失败");
    } finally {
      setLoading(false);
    }
  };

  const openTransferConfirmation = (settlement) => {
    if (!isFinance) return;
    setTransferTarget(settlement);
    setTransferDate(getJapanDate());
  };

  const confirmTransfer = async () => {
    if (!transferTarget?.id) return;
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements/${transferTarget.id}/mark-paid`, {
        transfer_date: transferDate,
      });
      setTransferTarget(null);
      await fetchSettlements();
      onOrderRefresh?.();
      window.alert("已标记为已转账");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "确认转账失败");
    } finally {
      setLoading(false);
    }
  };

  const unlockSettlement = async (settlement) => {
    if (!isAdmin) return;
    const password = window.prompt("请输入当前管理员登录密码：");
    if (!password) return;
    const first = window.confirm("第一次确认：该记录已完成转账。修改后仍保持已转账，并永久记录修改痕迹。是否继续？");
    if (!first) return;
    const second = window.confirm("第二次确认：请再次确认只修改实际结算金额或银行账户，不修改原派单价格。是否解锁？");
    if (!second) return;
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/settlements/${settlement.id}/unlock`, {
        password,
        first_confirmation: true,
        second_confirmation: true,
      });
      setForm(buildEditForm(settlement));
      setBankRawText("");
      setAiReview([]);
      setModal({
        mode: "edit",
        prepared: {
          order: {
            id: settlement.order_id,
            order_no: settlement.order_no,
            driver_name: settlement.driver_name,
            departure_date: settlement.departure_date,
            pickup_time: settlement.pickup_time,
            route: settlement.route,
            original_dispatch_price: settlement.original_dispatch_price,
          },
        },
        settlement,
        unlockToken: response.data.unlock_token,
      });
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "解锁失败");
    } finally {
      setLoading(false);
    }
  };

  const submitSettlementUpdate = async () => {
    if (!modal?.settlement?.id || !modal.unlockToken) return;
    const amount = Number(form.settlement_amount);
    const requiresPositiveAmount = ["additional_expense", "invoice_request"].includes(modal.settlement.settlement_type);
    if (!Number.isInteger(amount) || (requiresPositiveAmount ? amount <= 0 : amount < 0)) {
      window.alert(requiresPositiveAmount ? "该类结算金额必须是大于0的整数日元" : "实际结算金额必须是0以上的整数日元");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/settlements/${modal.settlement.id}`, {
        unlock_token: modal.unlockToken,
        settlement_amount: amount,
        adjustment_reason: form.adjustment_reason,
        bank_account: form.bank_account,
      });
      closeModal();
      await fetchSettlements();
      onOrderRefresh?.();
      window.alert("已转账记录已更新，原记录已保留在修改历史中");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "更新结算记录失败");
    } finally {
      setLoading(false);
    }
  };

  const createFinanceAccount = async (event) => {
    event.preventDefault();
    const submittedForm = {
      name: String(event.currentTarget.elements.namedItem("finance-name")?.value || financeForm.name).trim(),
      email: String(event.currentTarget.elements.namedItem("finance-email")?.value || financeForm.email).trim(),
      password: String(event.currentTarget.elements.namedItem("finance-password")?.value || financeForm.password),
    };
    if (!submittedForm.name || !submittedForm.email || !submittedForm.password) {
      window.alert("请填写姓名、登录邮箱和初始密码");
      return;
    }
    if (!submittedForm.email.includes("@")) {
      window.alert("请输入有效的财务邮箱");
      return;
    }
    if (submittedForm.password.length < 6) {
      window.alert("初始密码至少需要6个字符");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/settlements/finance/accounts`, submittedForm);
      setFinanceForm({ name: "", email: "", password: "" });
      await fetchFinanceAccounts();
      window.alert("财务账号已创建");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "创建财务账号失败");
    } finally {
      setLoading(false);
    }
  };

  const toggleFinanceAccount = async (account) => {
    const nextStatus = account.status === "approved" ? "suspended" : "approved";
    if (nextStatus === "suspended" && !window.confirm(`停用 ${account.name || account.email} 后，该账号当前登录会立即失效。是否继续？`)) {
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/settlements/finance/accounts/${account.id}/status`, { status: nextStatus });
      await fetchFinanceAccounts();
      window.alert(nextStatus === "approved" ? "财务账号已启用" : "财务账号已停用");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "更新财务账号状态失败");
    } finally {
      setLoading(false);
    }
  };

  const resetFinancePassword = async (account) => {
    const newPassword = window.prompt(`请输入 ${account.name || account.email} 的新密码（至少6位）：`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      window.alert("新密码至少需要6个字符");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${apiBaseUrl}/admin/settlements/finance/accounts/${account.id}/password`, { new_password: newPassword });
      window.alert("财务账号密码已重置，旧登录已失效");
    } catch (requestError) {
      window.alert(requestError.response?.data?.detail || "重置密码失败");
    } finally {
      setLoading(false);
    }
  };

  return {
    aiParsing,
    aiReview,
    bankRawText,
    data,
    error,
    exporting,
    financeAccounts,
    financeForm,
    form,
    invoiceRequestDownloadMonth,
    invoiceRequestDownloading,
    invoiceRequestFile,
    invoiceRequestForm,
    invoiceRequestUploading,
    invoiceOrderSearch,
    invoiceOrderSearchResults,
    invoiceOrderSearching,
    invoiceLinkModal,
    invoiceLinkSaving,
    invoiceLinkSearch,
    invoiceLinkSearchResults,
    invoiceLinkSearching,
    invoiceLinkSelectedOrderIds,
    isAdmin,
    isFinance,
    loading,
    modal,
    month,
    orderSearch,
    orderSearchResults,
    orderSearching,
    search,
    selectedInvoiceRequestIds,
    selectedInvoiceOrderIds,
    transferDate,
    transferTarget,
    todayJapan: getJapanDate(),
    closeModal,
    closeInvoiceOrderLinker,
    clearInvoiceLinkOrderSelection,
    confirmTransfer,
    createFinanceAccount,
    downloadInvoiceRequest,
    downloadMonthlyInvoiceRequests,
    downloadSelectedInvoiceRequests,
    exportMonthlyPaymentStatements,
    exportMonthlySettlements,
    fetchSettlements,
    openCreateSettlement,
    openAdditionalExpense,
    openInvoiceOrderLinker,
    openPendingSettlementEdit,
    openTransferConfirmation,
    parseBankAccountText,
    resetFinancePassword,
    setBankRawText,
    setFinanceForm,
    setForm,
    setInvoiceRequestDownloadMonth,
    setInvoiceRequestFile,
    setInvoiceRequestForm,
    setInvoiceOrderSearch,
    setInvoiceLinkSearch,
    setMonth,
    setOrderSearch,
    setSearch,
    setTransferDate,
    setTransferTarget,
    submitSettlement,
    submitAdditionalExpense,
    submitInvoiceRequest,
    submitPendingSettlementUpdate,
    submitSettlementUpdate,
    toggleFinanceAccount,
    toggleInvoiceRequestSelection,
    toggleInvoiceOrderSelection,
    toggleInvoiceLinkOrderSelection,
    unlockSettlement,
    updateBankField,
    voidPendingSettlement,
    searchSettlementOrders,
    searchInvoiceRequestOrders,
    searchInvoiceLinkOrders,
    saveInvoiceOrderLinks,
  };
}

export { emptyBankAccount, getJapanDate, getJapanMonth };
